import {
  Timestamp,
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { auth, db } from '../firebase';

type PartySettings = {
  maxQueueSize?: number;
  allowDuplicates?: boolean;
  requireAuth?: boolean;
  tier?: 'free' | 'premium';
  gameMode?: 'karaoke' | 'farkle' | 'imposter';
  isGameParty?: boolean;
};

const defaultPartySettings: Required<Pick<PartySettings, 'maxQueueSize' | 'allowDuplicates' | 'requireAuth' | 'tier'>> = {
  maxQueueSize: 5,
  allowDuplicates: false,
  requireAuth: false,
  tier: 'free',
};

export const mapPartySnapshot = (partyId: string, data: any) => ({
  id: partyId,
  name: data.name || 'Karaoke Party',
  createdBy: data.createdBy || '',
  createdAt: data.createdAt?.toDate?.() ?? new Date(),
  expiresAt: data.expiresAt?.toDate?.() ?? undefined,
  tier: data.tier,
  settings: {
    maxQueueSize: data.settings?.maxQueueSize ?? 5,
    allowDuplicates: data.settings?.allowDuplicates ?? false,
    requireAuth: data.settings?.requireAuth ?? false,
    ...(data.settings || {}),
  },
  currentSong: data.currentSong
    ? {
        ...data.currentSong,
        addedAt: data.currentSong.addedAt?.toDate?.() ?? undefined,
      }
    : null,
  isActive: data.isActive !== false,
});

export async function createReducedParty(name: string, settingsOverride?: PartySettings) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Sign in is required to create a party.');
  }

  const settings = {
    ...defaultPartySettings,
    ...(settingsOverride || {}),
  };
  const partyRef = doc(collection(db, 'parties'));
  const isFreeParty = settings.tier === 'free';
  const expiresAt = isFreeParty ? Timestamp.fromDate(new Date(Date.now() + 60 * 60 * 1000)) : null;

  await setDoc(partyRef, {
    id: partyRef.id,
    name: name || 'Karaoke Party',
    createdBy: user.uid,
    createdAt: serverTimestamp(),
    expiresAt,
    tier: settings.tier,
    gameMode: settingsOverride?.gameMode || 'karaoke',
    isGameParty: settingsOverride?.isGameParty === true,
    settings: {
      maxQueueSize: settings.maxQueueSize,
      allowDuplicates: settings.allowDuplicates,
      requireAuth: settings.requireAuth,
      tier: settings.tier,
      ...settingsOverride,
    },
    currentSong: null,
    isActive: true,
    hostPresent: true,
    hostLastActive: serverTimestamp(),
  });

  await setDoc(doc(db, 'parties', partyRef.id, 'activeMicrophones', 'host'), {
    userId: user.uid,
    isHost: true,
    createdAt: serverTimestamp(),
  });

  return { partyId: partyRef.id };
}

export async function joinReducedParty(partyId: string) {
  const partySnap = await getDoc(doc(db, 'parties', partyId));
  if (!partySnap.exists()) {
    throw new Error('Party not found');
  }

  const data = partySnap.data();
  if (data.expiresAt?.toDate?.() && data.expiresAt.toDate() < new Date()) {
    throw new Error('Party has expired');
  }
  if (data.isActive === false) {
    throw new Error('Party has ended');
  }

  return { party: mapPartySnapshot(partySnap.id, data) };
}

export async function addReducedQueueSong(
  partyId: string,
  song: Record<string, any>,
  singerName: string,
  viewerId?: string | null
) {
  const normalizedArtist = (song.artist || song.channelTitle || 'Unknown Artist') as string;
  const actorUid = auth.currentUser?.uid ?? null;
  const queueRef = collection(db, 'parties', partyId, 'queue');
  const payload = {
    ...song,
    videoId: song.videoId || song.id,
    artist: normalizedArtist,
    singerName,
    addedBy: actorUid || viewerId || 'guest',
    addedByDeviceId: viewerId || actorUid || `guest-${Date.now()}`,
    addedAt: serverTimestamp(),
    status: 'queued',
  };

  const addedRef = await addDoc(queueRef, payload);

  try {
    const partyRef = doc(db, 'parties', partyId);
    const partySnap = await getDoc(partyRef);
    const partyData = partySnap.data();
    if (partySnap.exists() && !partyData?.currentSong && auth.currentUser?.uid === partyData?.createdBy) {
      await updateDoc(addedRef, { status: 'playing' });
      await updateDoc(partyRef, {
        currentSong: {
          ...payload,
          id: addedRef.id,
          status: 'playing',
          addedAt: Timestamp.now(),
        },
      });
    }
  } catch {
    // Reduced mode tolerates missing auto-start privileges for guest submissions.
  }

  return { songId: addedRef.id };
}

export async function advanceReducedPartySong(partyId: string) {
  const partyRef = doc(db, 'parties', partyId);
  const partySnap = await getDoc(partyRef);
  if (!partySnap.exists()) {
    throw new Error('Party not found');
  }

  const partyData = partySnap.data();
  const currentSongId = partyData.currentSong?.id as string | undefined;
  const queuedSnap = await getDocs(
    query(
      collection(db, 'parties', partyId, 'queue'),
      where('status', '==', 'queued'),
      orderBy('addedAt'),
      limit(1)
    )
  );

  const batch = writeBatch(db);

  if (currentSongId) {
    batch.update(doc(db, 'parties', partyId, 'queue', currentSongId), {
      status: 'played',
      playedAt: serverTimestamp(),
    });
  }

  if (!queuedSnap.empty) {
    const nextDoc = queuedSnap.docs[0];
    const nextData = nextDoc.data();
    batch.update(nextDoc.ref, { status: 'playing' });
    batch.update(partyRef, {
      currentSong: {
        ...nextData,
        id: nextDoc.id,
        status: 'playing',
      },
    });
  } else {
    batch.update(partyRef, { currentSong: null });
  }

  await batch.commit();
}

export async function startReducedPartySong(partyId: string, songId: string) {
  const batch = writeBatch(db);
  batch.update(doc(db, 'parties', partyId, 'queue', songId), { status: 'playing' });
  batch.update(doc(db, 'parties', partyId), { 'currentSong.status': 'playing' });
  await batch.commit();
}